// @ts-ignore — package types come from emitted .d.ts at runtime.
import type { AuthSessionManagerType } from '@finder/user-login/lib/session-manager';

import { readSessionManager, whenSessionManager } from './session-manager-global';

import { applyAuthStateLoggedOut } from './auth-state';
import { clearAll as clearCache } from './cache';

const MAX_STALE_LOGOUT_ATTEMPTS = 1;

export class Session {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mgr: any;
  private listeners: Array<() => void> = [];

  constructor() {
    this.mgr = readSessionManager();

    if (this.mgr) {
      this.adopt();
    } else {
      whenSessionManager((mgr) => {
        this.mgr = mgr;
        this.adopt();
        this.notify();
      });
    }

    window.addEventListener('MembersCookiesSet', () => this.notify());
  }

  private adopt(): void {

    // session-manager throws "Client session can be open only once" on a
    // second openSession() call. v1 hides this with `.catch(() => {})` in
    // bootstrap, but consumers (rewards-email-sub-banner, comparison-platform,
    // chatbot, …) await openSession() unconditionally on the SAME singleton.
    // Make the method idempotent so the first call wins and later callers
    // resolve with the cached promise instead of throwing. The gnPatched
    // guard prevents double-wrapping if `new Session()` is constructed twice
    // (which would otherwise wrap our wrapper and cause infinite recursion).
    if (typeof this.mgr?.openSession === 'function' && !this.mgr.gnPatched) {
      const original = this.mgr.openSession.bind(this.mgr);
      let cached: Promise<void> | null = null;
      this.mgr.openSession = () => {
        if (!cached) cached = original().catch(() => undefined);
        return cached;
      };
      this.mgr.gnPatched = true;
      void Promise.resolve(this.mgr.openSession()).then(() => this.reconcileStaleSession());
    } else if (typeof this.mgr?.openSession === 'function') {
      void this.mgr.openSession();
    }

    if (typeof this.mgr.addLoginListener === 'function') {
      this.mgr.addLoginListener(() => this.notify());
    }
  }

  private reconcileStaleSession(): void {
    const authOptions = (
      window as unknown as { authOptions?: { disableBackendSessionCheck?: boolean } }
    ).authOptions;
    if (authOptions?.disableBackendSessionCheck) return;

    if (!document.body.classList.contains('user-authenticated')) return;

    // .dev TLD is cross-site with the Auth0 tenant (finder.com) so silent auth fails with 'login_required' even when logged in; skip the forced logout. QA uses same-site custom domains and reconciles like prod.
    if (window.location.hostname.endsWith('.dev')) return;

    const result =
      typeof this.mgr?.getLastSessionCheckResult === 'function'
        ? this.mgr?.getLastSessionCheckResult()
        : 'unknown';
    if (result !== 'unauthenticated') return;

    const target = new URL(window.location.href);
    const previousFid = target.searchParams.get('fid') ?? '';
    const attempt = /^\d+\./.test(previousFid) ? Number(previousFid.split('.')[0]) : 0;
    if (attempt >= MAX_STALE_LOGOUT_ATTEMPTS) return;

    applyAuthStateLoggedOut();

    target.searchParams.set('fid', `${attempt + 1}.${Math.floor(Math.random() * 1e9)}`);
    const returnTo = encodeURIComponent(target.href);
    window.location.href = `/auth?action=logout&noAuth0=true&returnTo=${returnTo}`;
  }

  isLoggedIn = (): boolean =>
    typeof this.mgr?.isUserLoggedIn === 'function' && this.mgr.isUserLoggedIn();

  getSessionData = (): unknown =>
    typeof this.mgr?.getSessionData === 'function' ? this.mgr.getSessionData() : null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getUserData = (): Promise<any> | null =>
    typeof this.mgr?.getUserData === 'function' ? this.mgr.getUserData() : null;

  logout = (): void => {
    clearCache();
    if (typeof this.mgr?.logout === 'function') this.mgr.logout();
  };

  getAccessToken = (): Promise<string | null> | null =>
    typeof this.mgr?.getAccessToken === 'function' ? this.mgr.getAccessToken() : null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRawManager = (): AuthSessionManagerType => this.mgr;

  onAuthChange(cb: () => void): () => void {
    this.listeners.push(cb);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== cb);
    };
  }

  onAuthSettled(cb: () => void): () => void {
    const awaitingLogin = () =>
      !this.isLoggedIn() && document.body.classList.contains('user-authenticated');

    let released = false;
    let timer = 0;

    const release = () => {
      released = true;
      window.clearTimeout(timer);
      cb();
    };

    const releaseWhenSettled = () => {
      if (released || !awaitingLogin()) release();
    };

    const offAuthChange = this.onAuthChange(releaseWhenSettled);

    if (awaitingLogin()) {
      timer = window.setTimeout(release, 3000);
      void Promise.resolve(this.mgr?.openSession?.())
        .catch(() => undefined)
        .then(releaseWhenSettled);
    } else {
      release();
    }

    return () => {
      window.clearTimeout(timer);
      offAuthChange();
    };
  }

  private notify() {
    this.listeners.forEach((l) => l());
  }
}
